import { createFileRoute } from '@tanstack/react-router'
import { CameraIcon, CameraOffIcon } from 'lucide-react'
import { useState } from 'react'

import { Page } from '@/components/page'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { CameraScanner } from '@/features/scan/CameraScanner'
import { ScanResultSheet } from '@/features/scan/ScanResultSheet'
import { useScanAction } from '@/features/scan/ScanHandler'

export const Route = createFileRoute('/scan')({ component: ScanPage })

function ScanPage() {
  const scan = useScanAction()
  const [camera, setCamera] = useState(false)
  const [code, setCode] = useState('')

  return (
    <Page
      title="Scan"
      description="Point a camera or a scanner at a label, or type the code under it."
    >
      <div className="mx-auto flex max-w-lg flex-col gap-4">
        {camera ? (
          <CameraScanner onCode={(found) => void scan.handle(found)} />
        ) : (
          <div className="text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
            A hand scanner works without the camera — just scan.
          </div>
        )}

        <Button variant="outline" onClick={() => setCamera((on) => !on)}>
          {camera ? <CameraOffIcon /> : <CameraIcon />}
          {camera ? 'Stop the camera' : 'Use the camera'}
        </Button>

        <form
          className="flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            const trimmed = code.trim()
            if (!trimmed) return
            setCode('')
            void scan.handle(trimmed)
          }}
        >
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="scan-code">Code</Label>
            <Input
              id="scan-code"
              value={code}
              autoFocus
              autoComplete="off"
              placeholder="ABCD1234"
              onChange={(event) => setCode(event.target.value)}
            />
          </div>
          <Button type="submit" disabled={scan.pending}>
            {scan.pending ? <Spinner className="size-4" /> : null}
            Look up
          </Button>
        </form>

        {scan.error && (
          <Alert variant="destructive">
            <AlertTitle>That did not work</AlertTitle>
            <AlertDescription>{scan.error}</AlertDescription>
          </Alert>
        )}
      </div>

      {scan.result && <ScanResultSheet result={scan.result} onClose={scan.clear} />}
    </Page>
  )
}
